import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

// The dissolve: two gradients multiplied (intersected), so the window fades
// out towards the right edge and towards the bottom edge, and is gone before
// either. Stops come from --mk-dissolve-x / --mk-dissolve-y. Autoprefixer
// writes the -webkit- twins (mask-composite: intersect becomes source-in).
const MASK = [
    '[mask-image:linear-gradient(to_right,#000_var(--mk-dissolve-x),transparent),linear-gradient(to_bottom,#000_var(--mk-dissolve-y),transparent)]',
    '[mask-composite:intersect]',
].join(' ');

// The masked box reaches this far up and left of the plate, so the plate's
// soft shadow shows on those sides too (the mask clips at the box's edge).
const SHADOW_ROOM = 12;

/** A dissolve stop, as a share of the plate's part of the box (past the shadow room). */
const stop = (percent) => `calc(${SHADOW_ROOM}px + (100% - ${SHADOW_ROOM}px) * ${percent / 100})`;

/**
 * A window onto the product inside a card: inset from the card's top left,
 * running past its right and bottom edges, and dissolving before it reaches
 * them, so no border is ever cut off by the card's edge. The window is a
 * white plate with a full hairline (drawn inside it, so the mask never clips
 * it), a 12px radius and the soft grey-tinted shadow; whatever it holds (a
 * vignette, or nothing yet while one loads) dissolves with it.
 *
 * Place it in a `relative` box of the size the window may take. The plate
 * starts 24px (32px from sm up) in from the box's top left and runs past its
 * right and bottom.
 *
 * - `fadeX` / `fadeY`: where the dissolve starts, in % of the plate's visible
 *   width and height (72 and 60 by default); the plate is drawn whole up to
 *   there. A large card can start later (say 78 and 66).
 * - `className`: the masked box's own classes; the ref and any other props
 *   land on it too.
 */
const DissolvingWindow = forwardRef(function DissolvingWindow(
    { children, fadeX = 72, fadeY = 60, className, style, ...props },
    ref,
) {
    return (
        <div
            ref={ref}
            className={cn('absolute bottom-0 left-3 right-0 top-3 sm:left-5 sm:top-5', MASK, className)}
            style={{ '--mk-dissolve-x': stop(fadeX), '--mk-dissolve-y': stop(fadeY), ...style }}
            {...props}
        >
            {/* 16px past the box on both open sides: its corners are
                always outside what the mask lets through. */}
            <div
                className={cn(
                    'absolute -bottom-4 -right-4 left-3 top-3 overflow-hidden rounded-xl bg-mk-surface shadow-mk-soft',
                    'after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:ring-1 after:ring-inset after:ring-mk-ink/10',
                )}
            >
                {children}
            </div>
        </div>
    );
});

export default DissolvingWindow;
